import React, { useState } from 'react';
import { formatDeliveryDate } from '../../services/dateUtils';
import { ErrorMessage, SuccessMessage } from '../Messages/StatusMessages';
import { batchSave } from '../../services/mealPlanService';
import { MEAL_TYPES, MEAL_PRICES } from '../../constants/meals';
// import axios from 'axios'; // Commented out - Stripe integration temporarily disabled

/**
 * Shared content component for Order Summary
 * Used by both SummaryModal (mobile) and OrderSummarySidebar (desktop)
 *
 * @param {string} variant - 'modal' or 'sidebar' to adjust styling
 * @param {object} pendingChanges - Pending changes tracked per delivery date
 * @param {function} onSaveSuccess - Callback when save is successful
 */
const OrderSummaryContent = ({
    ordersByDeliveryDate,
    deliveryDates,
    billingChanges,
    customerToken,
    pendingChanges = {},
    onSaveSuccess,
    variant = 'modal', // 'modal' or 'sidebar'
    customerDetails
}) => {
    const [paymentLoading, setPaymentLoading] = useState({});
    const [errorMessage, setErrorMessage] = useState('');
    const [confirmationModal, setConfirmationModal] = useState({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
    // eslint-disable-next-line no-unused-vars
    const [successMessage, setSuccessMessage] = useState(''); // Used for save-only success feedback

    // Define meal type order
    const mealTypeOrder = MEAL_TYPES.reduce((acc, mealType, index) => {
        acc[mealType] = index + 1;
        return acc;
    }, {});

    // Helper function for proper pluralization
    const pluralizeMealType = (mealType, count) => {
        if (count === 1) return mealType;
        if (mealType === 'Lunch') return 'Lunches';
        return `${mealType}s`;
    };

    // Styling adjustments based on variant
    const isSidebar = variant === 'sidebar';
    const contentPadding = isSidebar ? '20px' : '24px';
    //console.log('dateChanges.changes', pendingChanges);

    // Helper function to pendingChangesdetermine button text based on pending changes and net amount
    const getButtonText = (netAmount, isSkipped, hasPendingChanges) => {
        // No button if no pending changes and not skipped and no monetary changes
        if (!hasPendingChanges && netAmount === 0) return null;

        if (netAmount === 0) return 'Save Changes';
        if (netAmount > 0) return 'Process Payment to Save Changes';
        // netAmount < 0 (refund)
        if (isSkipped) return 'Process Refund';
        return 'Process Refund to Save Changes';
    };

    // Handle save-only (no payment needed)
    const handleSaveOnly = async (date) => {
        const dateChanges = pendingChanges[date];
        if (!dateChanges?.changes?.length) return;

        setPaymentLoading(prev => ({ ...prev, [date]: true }));
        try {
            const response = await batchSave(customerToken, date, dateChanges.changes, billingChanges);
            if (response.data.success) {
                setSuccessMessage(`Changes saved successfully for ${formatDeliveryDate(date)}`);
                setTimeout(() => setSuccessMessage(''), 5000);
                if (onSaveSuccess) {
                    onSaveSuccess(date, response.data.results);
                }
            } else {
                setErrorMessage(response.data.message || 'Failed to save changes. Please try again.');
            }
        } catch (error) {
            setErrorMessage('An error occurred while saving. Please try again.');
        } finally {
            setPaymentLoading(prev => ({ ...prev, [date]: false }));
        }
    };

    // Show confirmation modal when payment/save button is clicked
    // Accepts either a single date string or an array of dates
    const handleButtonClick = (dateOrDates) => {
        const dates = Array.isArray(dateOrDates) ? dateOrDates : [dateOrDates];
        const hasPendingChanges = dates.some(date => pendingChanges[date]?.isDirty);
        const hasRefund = dates.some(date => (billingChanges?.dates?.[date]?.net || 0) < 0);

        setConfirmationModal({ show: true, dates, isRefund: hasRefund, hasPendingChanges });
    };

    // Handle confirmed payment - saves changes to Airtable
    // Stripe integration temporarily disabled
    // Now supports saving multiple dates at once
    const handleConfirmedPayment = async () => {
        const dates = confirmationModal.dates;
        setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });

        // Set loading for all dates
        const loadingState = {};
        dates.forEach(date => { loadingState[date] = true; });
        setPaymentLoading(prev => ({ ...prev, ...loadingState }));

        const savedDates = [];
        const failedDates = [];

        try {
            // Save each date sequentially
            for (const date of dates) {
                const hasPendingChanges = pendingChanges[date]?.isDirty && pendingChanges[date]?.changes?.length > 0;

                if (hasPendingChanges) {
                    try {
                        const response = await batchSave(customerToken, date, pendingChanges[date].changes, billingChanges);
                        if (response.data.success) {
                            savedDates.push(date);
                            if (onSaveSuccess) {
                                onSaveSuccess(date, response.data.results);
                            }
                        } else {
                            failedDates.push(date);
                        }
                    } catch (error) {
                        console.error(`Save error for ${date}:`, error);
                        failedDates.push(date);
                    }
                }
            }

            // Show appropriate message
            if (savedDates.length > 0 && failedDates.length === 0) {
                const dateList = savedDates.map(d => formatDeliveryDate(d)).join(', ');
                setSuccessMessage(`Changes saved successfully for ${dateList}`);
                setTimeout(() => setSuccessMessage(''), 5000);
            } else if (failedDates.length > 0) {
                const failedList = failedDates.map(d => formatDeliveryDate(d)).join(', ');
                setErrorMessage(`Failed to save changes for: ${failedList}. Please try again.`);
            }
        } catch (error) {
            console.error('Save error:', error);
            setErrorMessage('An error occurred while saving. Please try again.');
        } finally {
            // Clear loading for all dates
            const clearedLoading = {};
            dates.forEach(date => { clearedLoading[date] = false; });
            setPaymentLoading(prev => ({ ...prev, ...clearedLoading }));
        }
    };

    // Get dates that should be displayed
    const displayDates = (deliveryDates || []).filter(date => {
        const hasOrders = ordersByDeliveryDate?.[date]?.some(order => order.quantity > 0);
        const hasBillingChanges = billingChanges?.dates?.[date]?.hasChanges || billingChanges?.dates?.[date]?.isSkipped;
        const hasPendingChanges = pendingChanges[date]?.isDirty || false;
        return hasOrders || hasBillingChanges || hasPendingChanges;
    });

    // Group orders by meal type for a given date
    const getOrdersByMealType = (date) => {
        const ordersForDate = ordersByDeliveryDate[date] || [];
        const selectedOrders = ordersForDate.filter(order => order.quantity > 0);

        // Group by meal type
        const grouped = {};
        selectedOrders.forEach(order => {
            const mealType = order.meal || 'Other';
            if (!grouped[mealType]) {
                grouped[mealType] = [];
            }
            // Check if order already exists, if so add to quantity
            const existing = grouped[mealType].find(o => o.itemName === order.itemName && o.proteinOptions?.find(opt => opt.isActive)?.price === 0 && order.proteinOptions?.find(opt => opt.isActive)?.price === 0);
            if (existing) {
                existing.quantity += order.quantity;
            } else {
                grouped[mealType].push({ ...order });
            }
        });

        return { grouped };
    };

    return (
        <div style={{
            padding: contentPadding,
            backgroundColor: '#f5f0eb',
            minHeight: isSidebar ? '100vh' : 'auto'
        }}>
            {/* Header */}
            <div>
                <h2 style={{
                    color: '#333',
                    fontSize: '18px',
                    fontWeight: '600',
                    marginTop: 0,
                    marginBottom: 0
                }}>
                    Order Summary
                </h2>
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    ⚠️ Meal edits (except for skipping a delivery) are NOT saved until you click the "Save Changes" button.
                </p>
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    ⚠️ Any unsaved changes will be lost if you close/refresh this page.
                </p>
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    🚌 Please note delivery fees (if applicable) are not included in the total amount (coming soon!).
                </p>
                <p style={{
                    color: 'rgb(115, 108, 106)',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginTop: 5,
                    marginBottom: '20px',
                    backgroundColor: 'rgba(248, 231, 40, 0.1)',
                    padding: '10px',
                    borderRadius: '10px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                }}>
                    🧾 We will process any billing adjustments within 5 business days after the delivery date.
                </p>

            </div>

            {/* Status Messages */}
            <ErrorMessage
                message={errorMessage}
                onClose={() => setErrorMessage('')}
            />
            <SuccessMessage
                message={successMessage}
                onClose={() => setSuccessMessage('')}
            />
            {(() => {
                const hasPendingChanges = displayDates.some(date => pendingChanges[date]?.isDirty) || false;
                const isDisabled = !hasPendingChanges || displayDates.some(date => paymentLoading[date]);

                return (
                    <button
                        onClick={() => handleButtonClick(displayDates.filter(date => pendingChanges[date]?.isDirty))}
                        disabled={isDisabled}
                        style={{
                            width: '90%',
                            backgroundColor: isDisabled ? '#ccc' : '#ff7c44',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '18px 16px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                            transition: 'all 0.2s ease',
                            margin: '12px auto 36px',
                            display: 'block',
                        }}
                        onMouseOver={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#e66a2e';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#ff7c44';
                            }
                        }}
                    >
                        {displayDates.some(date => paymentLoading[date])
                            ? 'Processing...'
                            : isDisabled ? 'No Changes to Save' : 'Save Changes'
                        }
                    </button>
                );
            })()}

            {displayDates.length === 0 ? (
                <div style={{
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '14px',
                    padding: '40px 20px'
                }}>
                    No meals selected for any delivery date.
                </div>
            ) : (
                <div>

                    {displayDates.map((date) => {
                        const dateData = billingChanges?.dates?.[date];
                        const { grouped } = getOrdersByMealType(date);

                        // Calculate net for this date
                        const netAmount = dateData?.net || 0;

                        return (
                            <div key={date} style={{ marginBottom: '24px' }}>
                                {/* Date Header */}
                                <div style={{
                                    fontWeight: '700',
                                    fontSize: '16px',
                                    color: '#333',
                                    marginBottom: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {formatDeliveryDate(date)} Delivery
                                    {dateData?.isSkipped && (
                                        <span style={{
                                            color: '#dc3545',
                                            fontWeight: '600',
                                            marginLeft: '8px',
                                            textTransform: 'none'
                                        }}>
                                            (Skipped)
                                        </span>
                                    )}
                                </div>

                                {/* Skipped Date - Simple meal type counts */}
                                {dateData?.isSkipped ? (
                                    <div style={{ marginBottom: '12px' }}>
                                        {dateData?.meals && Object.entries(dateData.meals)
                                            .filter(([_, data]) => data.subscribed > 0)
                                            .sort(([a], [b]) => (mealTypeOrder[a] || 999) - (mealTypeOrder[b] || 999))
                                            .map(([mealType, data], idx, arr) => {
                                                const refundAmount = data.amount || 0;
                                                return (
                                                    <div
                                                        key={mealType}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 0',
                                                            fontSize: '14px',
                                                            color: '#333',
                                                            borderBottom: idx < arr.length - 1 ? '1px dashed #ddd' : 'none'
                                                        }}
                                                    >
                                                        <span>0 / {data.subscribed} {pluralizeMealType(mealType, data.subscribed)}</span>
                                                        <span style={{
                                                            color: '#333',
                                                            fontStyle: 'normal',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            marginLeft: '8px',
                                                            whiteSpace: 'nowrap'
                                                        }}>
                                                            -${Math.abs(refundAmount).toFixed(2)}
                                                        </span>
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                ) : (
                                    /* Active Date - Detailed dish listing */
                                    <div style={{ marginBottom: '12px' }}>
                                        {/* Show message if no meals selected */}
                                        {Object.keys(grouped).length === 0 && (
                                            <div style={{
                                                color: '#666',
                                                fontSize: '13px',
                                                fontStyle: 'italic',
                                                padding: '8px 0'
                                            }}>
                                                {pendingChanges[date]?.isDirty
                                                    ? 'All meals for this date are removed (unsaved change)'
                                                    : 'No meals selected for this date.'}
                                            </div>
                                        )}
                                        {/* Get meal types from both orders and billing changes */}
                                        {(() => {
                                            return MEAL_TYPES.map((mealType, mealIdx) => {
                                                const mealData = dateData?.meals?.[mealType];
                                                const orders = grouped[mealType] || [];
                                                const ordered = mealData?.ordered ?? orders.reduce((sum, o) => sum + o.quantity, 0);
                                                const subscribed = mealData?.subscribed || 0;
                                                const delta = mealData?.delta || 0;
                                                const amount = mealData?.amount || 0;

                                                // Track how many meals are covered by subscription
                                                let remainingSubscribed = mealType === 'Snack' ? subscribed * customerDetails.customer.snacksPerDay : subscribed;

                                                return (subscribed > 0 || ordered > 0) && (
                                                    <div key={mealType} style={{ marginBottom: mealIdx < MEAL_TYPES.length - 1 ? '12px' : '0' }}>
                                                        {/* Meal Type Header - just shows count */}
                                                        <div style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            padding: '6px 0',
                                                            fontSize: '14px',
                                                            fontWeight: '600',
                                                            color: '#333',
                                                            borderBottom: '1px dashed #ddd'
                                                        }}>
                                                            <span>
                                                                {subscribed > 0 ? mealType === 'Snack' ? `${ordered} / ${subscribed} days of` : `${ordered} / ${subscribed}` : ordered} {pluralizeMealType(mealType, subscribed > 0 ? subscribed : ordered)}
                                                                {mealType === 'Snack' && (
                                                                    <span style={{
                                                                        fontSize: '12px',
                                                                        color: '#666',
                                                                        fontWeight: '400'
                                                                    }}>
                                                                        <br />({customerDetails.customer.snacksPerDay} snacks per day)
                                                                    </span>
                                                                )}
                                                            </span>

                                                            {delta < 0 && <span style={{
                                                                color: '#333',
                                                                fontStyle: 'normal',
                                                                fontSize: '13px',
                                                                fontWeight: '600',
                                                                marginLeft: '8px',
                                                                whiteSpace: 'nowrap'
                                                            }}>
                                                                -${Math.abs(amount).toFixed(2)}
                                                            </span>}

                                                        </div>

                                                        {/* Individual Dishes with per-meal pricing */}
                                                        {orders.map((order, orderIdx) => {
                                                            // Check if this dish has a premium protein
                                                            const activeProtein = order.proteinOptions?.find(opt => opt.isActive);
                                                            const hasPremiumProtein = activeProtein?.price > 0;
                                                            const isPremiumPaid = customerDetails?.subscriptions?.some(subscription => subscription?.meal?.toLowerCase().includes(order?.meal?.toLowerCase()) && subscription?.nextDeliveryDate?.toLowerCase() === order?.deliveryDate?.toLowerCase() && subscription?.tags?.includes('Premium Meat Only Paid'));

                                                            // Calculate per-meal pricing based on subscription coverage
                                                            const mealQuantity = order.quantity || 1;
                                                            const mealPrice = mealType === 'Add On' ? (order.price || 0) : MEAL_PRICES[mealType];

                                                            // Determine how many of this order are included vs extra
                                                            let includedCount = 0;
                                                            let extraCount = 0;

                                                            if (mealType === 'Add On') {
                                                                // Add-ons are never included in subscription
                                                                extraCount = mealQuantity;
                                                            } else if (remainingSubscribed >= mealQuantity) {
                                                                // All meals in this order are covered
                                                                includedCount = mealQuantity;
                                                                remainingSubscribed -= mealQuantity;
                                                            } else if (remainingSubscribed > 0) {
                                                                // Partially covered
                                                                includedCount = remainingSubscribed;
                                                                extraCount = mealQuantity - remainingSubscribed;
                                                                remainingSubscribed = 0;
                                                            } else {
                                                                // No subscription coverage left
                                                                extraCount = mealQuantity;
                                                            }

                                                            // Build display items (may need to show same meal twice if partially covered)
                                                            const displayItems = [];

                                                            if (includedCount > 0) {
                                                                displayItems.push({
                                                                    count: includedCount,
                                                                    isIncluded: true,
                                                                    price: 0
                                                                });
                                                            }

                                                            if (extraCount > 0) {
                                                                displayItems.push({
                                                                    count: extraCount,
                                                                    isIncluded: false,
                                                                    price: mealPrice
                                                                });
                                                            }

                                                            return displayItems.map((item, itemIdx) => (
                                                                <div
                                                                    key={`${orderIdx}-${itemIdx}`}
                                                                    style={{
                                                                        padding: '8px 0 8px 0',
                                                                        fontSize: '13px',
                                                                        color: '#333',
                                                                        borderBottom: orderIdx < orders.length - 1 || itemIdx < displayItems.length - 1 ? '1px solid #eee' : 'none'
                                                                    }}
                                                                >
                                                                    {/* Meal name row */}
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'flex-start'
                                                                    }}>
                                                                        <span style={{ flex: 1 }}>
                                                                            {order.itemName} {item.count > 1 ? `(×${item.count})` : ''}
                                                                        </span>
                                                                        <span style={{
                                                                            color: item.isIncluded ? '#666' : '#333',
                                                                            fontStyle: item.isIncluded ? 'italic' : 'normal',
                                                                            fontSize: item.isIncluded ? '12px' : '13px',
                                                                            fontWeight: item.isIncluded ? '400' : '600',
                                                                            marginLeft: '8px',
                                                                            whiteSpace: 'nowrap'
                                                                        }}>
                                                                            {item.isIncluded
                                                                                ? 'Included in plan'
                                                                                : `$${(item.price * item.count).toFixed(2)}`
                                                                            }
                                                                        </span>
                                                                    </div>

                                                                    {/* Premium protein row (only show once, with the first display item) */}
                                                                    {itemIdx === 0 && hasPremiumProtein && (
                                                                        <div style={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            paddingLeft: '12px',
                                                                            marginTop: '4px',
                                                                            fontSize: '12px',
                                                                            color: '#856404',
                                                                            fontStyle: 'italic'
                                                                        }}>
                                                                            <span>Premium Protein: {activeProtein.name}</span>
                                                                            {!isPremiumPaid && (
                                                                                <span>+${(activeProtein.price * mealQuantity).toFixed(2)}</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ));
                                                        })}
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                )}

                                {/* Subtotal, Tax, and Total for this date if there are monetary changes */}
                                {netAmount !== 0 && (
                                    <>
                                        {/* Subtotal */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '8px 0',
                                            borderTop: '1px solid #ccc',
                                            fontSize: '13px'
                                        }}>
                                            <span>Subtotal</span>
                                            <span style={{ color: '#333' }}>
                                                {netAmount > 0 ? `$${netAmount.toFixed(2)}` : `-$${Math.abs(netAmount).toFixed(2)}`}
                                            </span>
                                        </div>

                                        {/* Sales Tax - NYC rate 8.875% */}
                                        {(() => {
                                            const taxRate = 0.08875;
                                            const taxAmount = netAmount * taxRate;
                                            return (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    padding: '4px 0',
                                                    fontSize: '13px'
                                                }}>
                                                    <span>Sales Tax (8.875%)</span>
                                                    <span style={{ color: '#333' }}>
                                                        {taxAmount >= 0 ? `$${taxAmount.toFixed(2)}` : `-$${Math.abs(taxAmount).toFixed(2)}`}
                                                    </span>
                                                </div>
                                            );
                                        })()}

                                        {/* Total */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '8px 0',
                                            borderTop: '1px solid #ccc',
                                            fontWeight: '600',
                                            fontSize: '14px'
                                        }}>
                                            <span>Total</span>
                                            <span style={{ color: '#333' }}>
                                                {(() => {
                                                    const taxRate = 0.08875;
                                                    const totalWithTax = netAmount * (1 + taxRate);
                                                    return totalWithTax > 0 ? `$${totalWithTax.toFixed(2)}` : `-$${Math.abs(totalWithTax).toFixed(2)}`;
                                                })()}
                                            </span>
                                        </div>
                                    </>
                                )}

                                {/* Save/Payment Button - show if has pending changes OR monetary changes
                                {(() => {
                                    const hasPendingChanges = pendingChanges[date]?.isDirty || false;
                                    const buttonText = `Save Changes for ${formatDeliveryDate(date)}`;
                                    const isDisabled = !hasPendingChanges || paymentLoading[date];
                                    if (!buttonText) return null;

                                    return (
                                        <button
                                            onClick={() => handleButtonClick(date)}
                                            disabled={isDisabled}
                                            style={{
                                                width: '80%',
                                                backgroundColor: isDisabled ? '#ccc' : 'rgb(255, 177, 60)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '20px',
                                                padding: '10px 16px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                                transition: 'all 0.2s ease',
                                                margin: '12px auto 0',
                                                display: 'block',

                                            }}
                                            onMouseOver={(e) => {
                                                if (!isDisabled) {
                                                    e.target.style.backgroundColor = '#e66a2e';
                                                }
                                            }}
                                            onMouseOut={(e) => {
                                                if (!isDisabled) {
                                                    e.target.style.backgroundColor = 'rgb(255, 177, 60)';
                                                }
                                            }}
                                        >
                                            {paymentLoading[date]
                                                ? 'Processing...'
                                                : hasPendingChanges
                                                    ? buttonText
                                                    : `No Changes to Save for ${formatDeliveryDate(date)}`
                                            }
                                        </button>
                                    );
                                })()} */}

                                {/* Separator line between dates */}
                                {displayDates.indexOf(date) < displayDates.length - 1 && (
                                    <div style={{
                                        borderBottom: '3px solid #ccc',
                                        marginTop: '24px',
                                        marginBottom: '24px'
                                    }} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {(() => {
                const hasPendingChanges = displayDates.some(date => pendingChanges[date]?.isDirty) || false;
                const isDisabled = !hasPendingChanges || displayDates.some(date => paymentLoading[date]);

                return (
                    <button
                        onClick={() => handleButtonClick(displayDates.filter(date => pendingChanges[date]?.isDirty))}
                        disabled={isDisabled}
                        style={{
                            width: '90%',
                            backgroundColor: isDisabled ? '#ccc' : '#ff7c44',
                            color: 'white',
                            border: 'none',
                            borderRadius: '20px',
                            padding: '18px 16px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                            transition: 'all 0.2s ease',
                            margin: '12px auto 36px',
                            display: 'block',
                        }}
                        onMouseOver={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#e66a2e';
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isDisabled) {
                                e.target.style.backgroundColor = '#ff7c44';
                            }
                        }}
                    >
                        {displayDates.some(date => paymentLoading[date])
                            ? 'Processing...'
                            : isDisabled ? 'No Changes to Save' : 'Save Changes'
                        }
                    </button>
                );
            })()}

            {/* Contact Info */}
            <p style={{
                color: '#888',
                textAlign: 'center',
                fontSize: '12px',
                marginTop: '20px',
                marginBottom: 0
            }}>
                Questions? Contact us at <a href="mailto:team@getsquarefare.com" style={{ color: '#ff7c44' }}>team@getsquarefare.com</a>
            </p>

            {/* Confirmation Modal */}
            {confirmationModal.show && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false });
                        }
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            padding: '24px 14px',
                            maxWidth: '600px',
                            width: '90%',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{
                            marginTop: 0,
                            marginBottom: '16px',
                            color: '#333',
                            fontSize: '24px',
                            fontWeight: '600',
                            textAlign: 'center'
                        }}>
                            Confirm Meal Edits
                        </h3>

                        <p style={{
                            color: '#666',
                            fontSize: '15px',
                            lineHeight: '1.6',
                            marginBottom: '20px',
                            textAlign: 'center'
                        }}>
                            {confirmationModal.dates.length === 1 ? (
                                <>ALL meal customizations for <b>{formatDeliveryDate(confirmationModal.dates[0])}</b> delivery will be saved.</>
                            ) : (
                                <>ALL meal customizations for the following deliveries will be saved: <b>{confirmationModal.dates.map(d => formatDeliveryDate(d)).join(', ')}</b></>
                            )}
                            <br /><br />
                            We will process any billing adjustments within 5 business days after the delivery date. Please note delivery fees (if applicable) are not included in the total amount (coming soon!).
                        </p>

                        <div style={{
                            display: 'flex',
                            gap: '12px'
                        }}>
                            <button
                                onClick={() => setConfirmationModal({ show: false, dates: [], isRefund: false, hasPendingChanges: false })}
                                style={{
                                    flex: 1,
                                    padding: '12px 20px',
                                    backgroundColor: '#fff',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmedPayment}
                                style={{
                                    flex: 1,
                                    padding: '12px 20px',
                                    backgroundColor: '#ff7c44',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OrderSummaryContent;
